import { getRecoil, resetRecoil, setRecoil } from 'recoil-nexus'

import { currentTokenState, RefreshTokenResponse, SavedTokens } from 'services/auth'
import { errorMessage } from 'shared/error-handle'
import { API_URL } from '../../../constants'
import { RequestMethodOptions, ApiResponse, ApiVersion } from './types'

export class HTTPNotAuthorizedException extends Error {
  constructor(message?: string) {
    super(`Вы не авторизованы\n${message ?? ''}`)
  }
}

export class HttpClient {
  private readonly baseUrl: string = API_URL

  private readonly basePath: string

  private readonly apiVersion: ApiVersion

  constructor(basePath: string, apiVersion: ApiVersion = 'v1') {
    this.basePath = basePath
    this.apiVersion = apiVersion
  }

  private async refresh(tokens: SavedTokens | null): Promise<void> {
    if (!tokens) {
      throw new HTTPNotAuthorizedException()
    }

    const response = await fetch(new URL('v1/auth/refresh', this.baseUrl), {
      method: 'POST',
      body: JSON.stringify({ refreshToken: tokens.refresh }),
    })

    const newTokens: RefreshTokenResponse & { message?: string } = await response.json()

    if (response.status !== 200) {
      throw new HTTPNotAuthorizedException(newTokens.message)
    }

    setRecoil(currentTokenState, {
      access: newTokens.accessToken,
      refresh: newTokens.refreshToken,
    })
  }

  private getHeaders(options: RequestInit, tokens: SavedTokens | null): HeadersInit {
    const headers: HeadersInit & { Authorization?: string } = { ...options.headers }

    if (!this.basePath.includes('/auth')) {
      if (!tokens) {
        throw new HTTPNotAuthorizedException()
      }

      headers.Authorization = tokens.access
    }

    return headers
  }

  private async request<T>(
    url: URL,
    options: RequestInit = {},
    retry: boolean = true
  ): Promise<ApiResponse<T>> {
    try {
      const tokens = getRecoil(currentTokenState)
      const headers: HeadersInit = this.getHeaders(options, tokens)
      const res = await fetch(url, { ...options, headers })

      if (res.status === 401 && retry) {
        await this.refresh(tokens)
        return await this.request(url, options, false)
      }

      const resData = await res.json()

      return {
        status: res.status,
        error: resData?.message,
        data: resData,
        ok: res.status < 400,
      }
    } catch (error) {
      const errMsg = errorMessage(error)
      /* eslint-disable-next-line no-console */
      console.error(errMsg)
      if (error instanceof HTTPNotAuthorizedException) {
        resetRecoil(currentTokenState)
      }

      return {
        status: 401,
        data: null as T,
        error: errMsg,
        ok: false,
      }
    }
  }

  public async get<T>(path: string = '', options?: Omit<RequestMethodOptions, 'body'>) {
    return this.request<T>(this.buildUrl(path, options?.query), { ...options, method: 'GET' })
  }

  public async post<T>(path: string, options?: RequestMethodOptions) {
    return this.request<T>(this.buildUrl(path, options?.query), {
      ...options,
      method: 'POST',
      body: options?.body ? JSON.stringify(options?.body) : undefined,
    })
  }

  public async put<T>(path: string, options?: RequestMethodOptions) {
    return this.request<T>(this.buildUrl(path, options?.query), {
      ...options,
      method: 'PUT',
      body: options?.body ? JSON.stringify(options?.body) : undefined,
    })
  }

  public async del<T>(path: string, options?: RequestMethodOptions) {
    return this.request<T>(this.buildUrl(path, options?.query), {
      ...options,
      method: 'DELETE',
      body: options?.body ? JSON.stringify(options?.body) : undefined,
    })
  }

  public async patch<T>(path: string, options?: RequestMethodOptions) {
    return this.request<T>(this.buildUrl(path, options?.query), {
      ...options,
      method: 'PATCH',
      body: options?.body ? JSON.stringify(options?.body) : undefined,
    })
  }

  protected buildUrl(path: string, query: Record<string, string | number> = {}): URL {
    const url = new URL(`${this.apiVersion}${this.basePath}${path}`, this.baseUrl)

    /* eslint-disable-next-line guard-for-in */
    for (const queryKey in query) {
      url.searchParams.append(queryKey, String(query[queryKey]))
    }

    return url
  }
}
